import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'

/**
 * Invite / recovery links redirect here; Supabase client establishes session from URL, then user sets password.
 */
export default function FinishInvitePage() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  // Read the flow type from the URL hash before Supabase's async initialize() clears it.
  // React's synchronous initial render runs before that async work, so this is reliable.
  const [flowType] = useState(() => {
    const hash = window.location.hash
    return hash.includes('type=recovery') ? 'recovery' : 'invite'
  })

  const heading = flowType === 'recovery' ? 'Reset your password' : 'Set your password'
  const subtext =
    flowType === 'recovery'
      ? 'Choose a new password for your SMG account, then continue to the dashboard.'
      : 'Choose a password for your SMG account, then continue to the dashboard.'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setBusy(true)
    const { error: upErr } = await supabase.auth.updateUser({ password })
    setBusy(false)
    if (upErr) {
      setError(upErr.message || 'Could not set password.')
      return
    }
    setDone(true)
    setTimeout(() => navigate('/', { replace: true }), 1500)
  }

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#111827',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9ca3af',
        }}
      >
        Loading…
      </div>
    )
  }

  if (!user) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#111827',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <p style={{ color: '#e5e7eb', marginBottom: 12 }}>No active session.</p>
          <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 20 }}>
            Open the link from your invitation email in this browser, or sign in if you already have a password.
          </p>
          <Link to="/login" style={{ color: '#FACC15', fontWeight: 600 }}>
            Go to login
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#111827',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          backgroundColor: '#1f2937',
          borderRadius: 16,
          border: '1px solid rgba(255,255,255,0.07)',
          padding: 40,
          width: '100%',
          maxWidth: 400,
        }}
      >
        {done ? (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                backgroundColor: 'rgba(250,204,21,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FACC15" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={{ color: 'white', fontSize: 20, fontWeight: 700, margin: '0 0 10px' }}>Password saved</h2>
            <p style={{ color: '#9ca3af', fontSize: 14, margin: 0 }}>Taking you to the dashboard…</p>
          </div>
        ) : (
          <>
            <h1 style={{ color: 'white', fontSize: 22, fontWeight: 700, margin: '0 0 8px 0' }}>{heading}</h1>
            <p style={{ color: '#9ca3af', fontSize: 14, margin: '0 0 24px 0' }}>{subtext}</p>
            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: 14, marginBottom: 6 }}>New password</label>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: 8,
                  border: '1px solid #374151',
                  backgroundColor: '#111827',
                  color: 'white',
                  marginBottom: 16,
                  boxSizing: 'border-box',
                }}
              />
              <label style={{ display: 'block', color: '#9ca3af', fontSize: 14, marginBottom: 6 }}>Confirm password</label>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: 8,
                  border: '1px solid #374151',
                  backgroundColor: '#111827',
                  color: 'white',
                  marginBottom: 16,
                  boxSizing: 'border-box',
                }}
              />
              {error ? (
                <p style={{ color: '#f87171', fontSize: 14, margin: '0 0 12px 0' }}>{error}</p>
              ) : null}
              <button
                type="submit"
                disabled={busy}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: 'none',
                  backgroundColor: busy ? '#a16207' : '#FACC15',
                  color: '#111827',
                  fontWeight: 600,
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? 'Saving…' : 'Continue'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
